import React, { ComponentType, useState, useEffect } from 'react';
import { RemoteLoader } from '../../services/RemoteLoader';
import { Spinner } from 'src/components/Spinner';
import styles from './RemoteComponent.module.scss';

const DefaultFallback: React.FC<{ remoteName: string }> = ({ remoteName }) => (
  <div className="p-5 border-2 border-dashed border-gray-300 rounded-lg text-center bg-gray-50 text-gray-600">
    <h3 className="text-lg font-semibold mb-2">Remote App Unavailable</h3>
    <p className="mb-1">
      The <span className="font-semibold text-blue-600 bg-blue-100 px-2 py-1 rounded">{remoteName}</span> application is
      currently not running or unreachable.
    </p>
    <p>Please start the remote application or check your network connection.</p>
  </div>
);

export const useRemoteComponent = (remoteName: string, module: string) => {
  const [Component, setComponent] = useState<ComponentType | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadRemoteComponent = async () => {
      try {
        setLoading(true);
        setError(null);

        const remoteComponent = await RemoteLoader.loadRemote(remoteName, module);

        if (remoteComponent) {
          setComponent(() => remoteComponent);
        } else {
          setError('Failed to load remote component');
        }
      } catch (err) {
        console.warn(`Failed to load remote ${remoteName}/${module}:`, err);
        setError(err instanceof Error ? err.message : 'Unknown error');
      } finally {
        setLoading(false);
      }
    };

    loadRemoteComponent();
  }, [remoteName, module]);

  return { Component, loading, error };
};

export const RemoteComponent: React.FC<{
  remoteName: string;
  module: string;
  fallbackComponent?: ComponentType<{ remoteName: string }>;
  [key: string]: any;
}> = ({ remoteName, module, fallbackComponent = DefaultFallback, ...props }) => {
  const { Component, loading, error } = useRemoteComponent(remoteName, module);
  const FallbackComponent = fallbackComponent;

  if (loading) {
    return (
      <div className={styles['spinner-wrapper']}>
        <Spinner />
      </div>
    );
  }

  if (error || !Component) {
    return <FallbackComponent remoteName={remoteName} />;
  }

  return <Component {...props} />;
};

export class ErrorBoundary extends React.Component<
  {
    children: React.ReactNode;
    fallback?: ComponentType<{ remoteName: string }>;
    remoteName: string;
  },
  { hasError: boolean }
> {
  constructor(props: any) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): { hasError: boolean } {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error(`Remote app error for ${this.props.remoteName}:`, error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      const FallbackComponent = this.props.fallback || DefaultFallback;
      return <FallbackComponent remoteName={this.props.remoteName} />;
    }

    return this.props.children;
  }
}

export { DefaultFallback };
